/**
 * Market Profile (TPO) — Time Price Opportunity
 * Shows how many time periods (bars) were spent at each price level.
 * Visible-range based, updates on scroll/zoom like Volume Profile.
 */

const NUM_BINS = 60;
const VALUE_AREA_PCT = 0.70;

class MarketProfilePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      s._computeFromVisibleRange();
      const { bins, pocIndex, vahIndex, valIndex, maxCount } = s._computed;
      if (!bins || bins.length === 0 || !s._series) return;

      const width = bs.width / hpr;
      const barMaxWidth = width * 0.12;

      ctx.save();

      // Draw TPO blocks as a heatmap
      for (let i = 0; i < bins.length; i++) {
        const bin = bins[i];
        if (bin.count === 0) continue;

        const yTop = s._series.priceToCoordinate(bin.high);
        const yBot = s._series.priceToCoordinate(bin.low);
        if (yTop == null || yBot == null) continue;

        const barH = Math.max(Math.abs(yBot - yTop) * vpr, 1);
        const intensity = bin.count / maxCount;
        const barW = intensity * barMaxWidth * hpr;
        const x = 4 * hpr; // align to left edge

        // Heatmap color: cold (blue) → hot (red)
        if (i === pocIndex) {
          ctx.fillStyle = '#ef4444';
          ctx.globalAlpha = 0.7;
        } else if (i >= valIndex && i <= vahIndex) {
          const t = intensity;
          const r = Math.round(99 + t * 156);
          const g = Math.round(102 + (1 - t) * 50);
          const b = Math.round(241 - t * 100);
          ctx.fillStyle = `rgb(${r},${g},${b})`;
          ctx.globalAlpha = 0.4;
        } else {
          ctx.fillStyle = '#6366f1';
          ctx.globalAlpha = 0.15;
        }

        const y = Math.min(yTop, yBot) * vpr;
        ctx.fillRect(x, y, barW, barH);

        // TPO letter count
        if (barH > 8 * vpr && barW > 15 * hpr) {
          ctx.globalAlpha = 0.7;
          ctx.fillStyle = 'var(--text-primary)';
          ctx.font = `${8 * hpr}px -apple-system, sans-serif`;
          ctx.fillText(String(bin.count), x + 2 * hpr, y + barH / 2 + 3 * vpr);
        }
      }

      // POC marker
      if (pocIndex >= 0 && pocIndex < bins.length) {
        const pocPrice = (bins[pocIndex].high + bins[pocIndex].low) / 2;
        const pocY = s._series.priceToCoordinate(pocPrice);
        if (pocY != null) {
          ctx.globalAlpha = 0.8;
          ctx.fillStyle = '#ef4444';
          ctx.font = `bold ${9 * hpr}px -apple-system, sans-serif`;
          ctx.fillText(`TPO-POC`, 4 * hpr, pocY * vpr - 3 * vpr);
        }
      }

      ctx.restore();
    })}};
  }
}

export class MarketProfilePrimitive {
  constructor() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    this._view = new MarketProfilePV(this);
    this._computed = { bins: [], pocIndex: -1, vahIndex: -1, valIndex: -1, maxCount: 0 };
    this._candles = [];
    this._lastRangeKey = '';
  }

  attached({ requestUpdate, chart, series }) {
    this._requestUpdate = requestUpdate;
    this._chart = chart;
    this._series = series;
    this._onRangeChange = () => this._requestUpdate?.();
    chart.timeScale().subscribeVisibleLogicalRangeChange(this._onRangeChange);
  }

  detached() {
    if (this._chart && this._onRangeChange) {
      try { this._chart.timeScale().unsubscribeVisibleLogicalRangeChange(this._onRangeChange); } catch {}
    }
    this._requestUpdate = null; this._chart = null; this._series = null;
  }

  updateCandles(candles) {
    this._candles = candles;
    this._lastRangeKey = '';
    this._requestUpdate?.();
  }

  _computeFromVisibleRange() {
    if (!this._chart || !this._candles || this._candles.length < 5) return;
    const ts = this._chart.timeScale();
    const range = ts.getVisibleLogicalRange();
    if (!range) return;

    const from = Math.max(0, Math.floor(range.from));
    const to = Math.min(this._candles.length - 1, Math.ceil(range.to));
    const rangeKey = `mp-${from}-${to}`;
    if (rangeKey === this._lastRangeKey) return;
    this._lastRangeKey = rangeKey;

    const visible = this._candles.slice(from, to + 1);
    this._compute(visible);
  }

  _compute(candles) {
    if (!candles || candles.length < 5) {
      this._computed = { bins: [], pocIndex: -1, vahIndex: -1, valIndex: -1, maxCount: 0 };
      return;
    }

    let minPrice = Infinity, maxPrice = -Infinity;
    for (const c of candles) {
      if (c.low < minPrice) minPrice = c.low;
      if (c.high > maxPrice) maxPrice = c.high;
    }
    const range = maxPrice - minPrice;
    if (range <= 0) return;
    const binSize = range / NUM_BINS;

    const bins = [];
    for (let i = 0; i < NUM_BINS; i++) {
      bins.push({ low: minPrice + i * binSize, high: minPrice + (i + 1) * binSize, count: 0 });
    }

    // Count time (bars) spent at each level
    for (const c of candles) {
      for (let i = 0; i < NUM_BINS; i++) {
        if (c.high >= bins[i].low && c.low <= bins[i].high) {
          bins[i].count++;
        }
      }
    }

    let maxCount = 0, pocIndex = 0;
    for (let i = 0; i < bins.length; i++) {
      if (bins[i].count > maxCount) { maxCount = bins[i].count; pocIndex = i; }
    }

    // Value area
    const totalCount = bins.reduce((s, b) => s + b.count, 0);
    const targetCount = totalCount * VALUE_AREA_PCT;
    let vaCount = bins[pocIndex].count;
    let vaLow = pocIndex, vaHigh = pocIndex;
    while (vaCount < targetCount && (vaLow > 0 || vaHigh < bins.length - 1)) {
      const below = vaLow > 0 ? bins[vaLow - 1].count : 0;
      const above = vaHigh < bins.length - 1 ? bins[vaHigh + 1].count : 0;
      if (below >= above && vaLow > 0) { vaLow--; vaCount += bins[vaLow].count; }
      else if (vaHigh < bins.length - 1) { vaHigh++; vaCount += bins[vaHigh].count; }
      else break;
    }

    this._computed = { bins, pocIndex, vahIndex: vaHigh, valIndex: vaLow, maxCount };
  }

  updateAllViews() {}
  paneViews() { return [this._view]; }
}
